// QGIS Service for Authenticated Layer Streaming
import { authService } from '../auth/authService';
import { apiConfig } from '../auth/authConfig';

class QGISService {
  constructor() {
    // Use the same configuration as the working legacy API
    this.baseUrl = process.env.NODE_ENV === 'production' 
      ? apiConfig.production.baseUrl 
      : apiConfig.development.baseUrl;
    
    // QGIS Project Configuration
    // Using STAGING Worldview project - contains all quartz claims layers with proper styling
    this.qgisProject = {
      name: 'Worldview',
      path: '/var/www/qgis-projects/azure/projects/staging/Worldview.qgz',
      environment: 'staging',
      description: 'Selkirk Copper Worldview Project (Staging)'
    };
    
    this.activeStreams = new Map();
  }

  async getAuthenticatedHeaders() {
    try {
      const token = await authService.getAccessToken('qgisApi');
      
      return {
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/xml, text/xml, */*',
        'Cache-Control': 'no-cache'
      };
    } catch (error) {
      console.error('❌ Failed to get authentication token:', error);
      throw new Error(`Authentication failed: ${error.message}`);
    }
  }

  async getWMSCapabilities(project = null) {
    try {
      const projectPath = project || this.qgisProject.path;
      const headers = await this.getAuthenticatedHeaders();
      
      let url = `${this.baseUrl}/qgis/qgis_mapserv.fcgi?SERVICE=WMS&REQUEST=GetCapabilities&VERSION=1.3.0&MAP=${projectPath}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`WMS GetCapabilities failed: ${response.status} ${response.statusText}`);
      }

      const xmlText = await response.text();
      
      // Check if response is HTML (error page) instead of XML
      if (xmlText.trim().toLowerCase().startsWith('<!doctype html') || 
          xmlText.trim().toLowerCase().startsWith('<html')) {
        console.error('❌ Server returned HTML instead of XML - likely authentication error');
        throw new Error('Server returned HTML error page instead of WMS capabilities XML. Check authentication.');
      }
      
      return this.parseWMSCapabilities(xmlText);
      
    } catch (error) {
      console.error('❌ Failed to get WMS capabilities:', error);
      throw error;
    }
  }

  parseWMSCapabilities(xmlText) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
    
    // Check for parsing errors
    const parseError = xmlDoc.querySelector('parsererror');
    if (parseError) {
      throw new Error('Invalid XML response from QGIS server');
    }

    const capabilities = {
      service: {
        name: xmlDoc.querySelector('Service Name')?.textContent || 'Unknown',
        title: xmlDoc.querySelector('Service Title')?.textContent || 'QGIS Server',
        abstract: xmlDoc.querySelector('Service Abstract')?.textContent || '',
        version: xmlDoc.querySelector('WMS_Capabilities')?.getAttribute('version') || '1.3.0'
      },
      layers: [],
      crs: [],
      formats: []
    };

    // Extract supported CRS
    const crsElements = xmlDoc.querySelectorAll('Layer > CRS');
    crsElements.forEach(crs => {
      const crsValue = crs.textContent.trim();
      if (crsValue && !capabilities.crs.includes(crsValue)) {
        capabilities.crs.push(crsValue);
      }
    });

    // Extract supported formats
    const formatElements = xmlDoc.querySelectorAll('GetMap Format');
    formatElements.forEach(format => {
      const formatValue = format.textContent.trim();
      if (formatValue && !capabilities.formats.includes(formatValue)) {
        capabilities.formats.push(formatValue);
      }
    });

    // Extract layer information
    // Skip root container layers - only get layers with actual names (not empty or "Untitled")
    const layerElements = xmlDoc.querySelectorAll('Layer[queryable="1"]');
    
    layerElements.forEach(layer => {
      const name = layer.querySelector('Name')?.textContent?.trim();
      const title = layer.querySelector('Title')?.textContent?.trim();
      const abstract = layer.querySelector('Abstract')?.textContent?.trim() || '';
      
      if (name && name.length > 0 && title) {
        // Extract bounding box
        const bbox = layer.querySelector('BoundingBox, LatLonBoundingBox, EX_GeographicBoundingBox');
        let boundingBox = null;
        
        if (bbox) {
          boundingBox = {
            minx: parseFloat(bbox.getAttribute('minx') || bbox.getAttribute('westBoundLongitude')),
            miny: parseFloat(bbox.getAttribute('miny') || bbox.getAttribute('southBoundLatitude')),
            maxx: parseFloat(bbox.getAttribute('maxx') || bbox.getAttribute('eastBoundLongitude')),
            maxy: parseFloat(bbox.getAttribute('maxy') || bbox.getAttribute('northBoundLatitude')),
            crs: bbox.getAttribute('CRS') || bbox.getAttribute('SRS') || 'EPSG:4326'
          };
        }

        capabilities.layers.push({
          name: name,
          title: title,
          abstract: abstract,
          queryable: true,
          boundingBox: boundingBox,
          type: 'WMS'
        });
      }
    });

    return capabilities;
  }

  async getWFSCapabilities(project = null) {
    try {
      const projectPath = project || this.qgisProject.path;
      const headers = await this.getAuthenticatedHeaders();
      
      let url = `${this.baseUrl}/qgis/qgis_mapserv.fcgi?SERVICE=WFS&REQUEST=GetCapabilities&MAP=${projectPath}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`WFS GetCapabilities failed: ${response.status} ${response.statusText}`);
      }

      const xmlText = await response.text();
      
      return this.parseWFSCapabilities(xmlText);
      
    } catch (error) {
      console.error('❌ Failed to get WFS capabilities:', error);
      throw error;
    }
  }

  parseWFSCapabilities(xmlText) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
    
    const capabilities = {
      service: {
        name: xmlDoc.querySelector('Service Name')?.textContent || 'WFS',
        title: xmlDoc.querySelector('Service Title')?.textContent || 'QGIS WFS Server',
        version: xmlDoc.querySelector('WFS_Capabilities')?.getAttribute('version') || '2.0.0'
      },
      featureTypes: []
    };

    // Extract feature types (vector layers)
    const featureTypeElements = xmlDoc.querySelectorAll('FeatureType');
    
    featureTypeElements.forEach(featureType => {
      const name = featureType.querySelector('Name')?.textContent;
      const title = featureType.querySelector('Title')?.textContent;
      const abstract = featureType.querySelector('Abstract')?.textContent || '';
      
      if (name && title) {
        capabilities.featureTypes.push({
          name: name,
          title: title,
          abstract: abstract,
          type: 'WFS',
          outputFormats: ['GeoJSON', 'GML', 'KML']
        });
      }
    });

    return capabilities;
  }

  // Create authenticated WMS tile layer for Leaflet
  createAuthenticatedWMSTileLayer(layerName, options = {}) {
    const baseUrl = `${this.baseUrl}/qgis/qgis_mapserv.fcgi`;
    
    return L.tileLayer.wms(baseUrl, {
      layers: layerName,
      format: 'image/png',
      transparent: true,
      version: '1.3.0',
      crs: L.CRS.EPSG3857,
      attribution: '© Selkirk Copper - QGIS Server',
      
      // Custom beforeRequest to add authentication
      beforeRequest: async (xhr) => {
        try {
          const token = await authService.getAccessToken();
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        } catch (error) {
          console.error('Failed to get auth token for WMS request:', error);
        }
      },
      
      ...options
    });
  }

  // Load GeoJSON features from WFS with authentication
  async loadWFSFeatures(layerName, bounds = null) {
    try {
      const headers = await this.getAuthenticatedHeaders();
      
      let url = `${this.baseUrl}/qgis/qgis_mapserv.fcgi?` +
        `SERVICE=WFS&` +
        `REQUEST=GetFeature&` +
        `VERSION=2.0.0&` +
        `TYPENAME=${layerName}&` +
        `OUTPUTFORMAT=application/json&` +
        `SRSNAME=EPSG:4326`;

      // Add bounding box filter if provided
      if (bounds) {
        const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()},EPSG:4326`;
        url += `&BBOX=${bbox}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${await authService.getAccessToken()}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`WFS GetFeature failed: ${response.status} ${response.statusText}`);
      }

      const geoJson = await response.json();
      return geoJson;
      
    } catch (error) {
      console.error(`Failed to load WFS features for ${layerName}:`, error);
      throw error;
    }
  }

  // Health check for QGIS server
  async healthCheck() {
    try {
      const headers = await this.getAuthenticatedHeaders();
      const url = `${this.baseUrl}/health`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: headers
      });

      if (response.ok) {
        const result = await response.text();
        return { status: 'healthy', message: result };
      } else {
        const errorText = await response.text();
        console.error('❌ Health check failed:', response.status, response.statusText);
        return { status: 'error', message: `Health check failed: ${response.status} - ${errorText.substring(0, 100)}` };
      }
    } catch (error) {
      console.error('❌ Health check exception:', error);
      return { status: 'error', message: error.message };
    }
  }
}

export const qgisService = new QGISService();
